import { Alert, AlertDescription } from "@bcc/components/ui/alert";
import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Checkbox } from "@bcc/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { Textarea } from "@bcc/components/ui/textarea";
import {
  fetchShopifyProducts,
  importProduct,
  type ProductImportConfig,
  type ShopifyProduct,
} from "@bcc/lib/shopify-service";
import {
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Package,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { setShopifyImportModalTestId } from "./shopify-import-modal.utils";

interface ShopifyImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
  testId?: string;
}

type Step = "select" | "configure" | "review";

export function ShopifyImportModal({
  open,
  onOpenChange,
  onImportComplete,
  testId,
}: ShopifyImportModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>("select");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  // Configuration state
  const [config, setConfig] = useState<Partial<ProductImportConfig>>({
    commission: 15,
    margin: 30,
    category: "Espresso Blend",
    selectedVariants: [],
  });

  useEffect(() => {
    if (open && step === "select") {
      loadProducts();
    }
  }, [open, step]);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchShopifyProducts();
      setProducts(data);
    } catch (err) {
      setError(t("views.shopifyImportModal.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product: ShopifyProduct) => {
    setSelectedProduct(product);
    setConfig({
      ...config,
      shopifyProductId: product.id,
      title: product.title,
      description: product.description,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      images: product.images,
      tags: product.tags,
      selectedVariants: product.variants.map((v) => v.id),
    });
    setStep("configure");
  };

  const handleImport = async () => {
    if (!config.shopifyProductId) return;

    setImporting(true);
    setError(null);

    try {
      const result = await importProduct();

      if (result.success) {
        setStep("review");
        setTimeout(() => {
          onImportComplete();
          onOpenChange(false);
          resetModal();
        }, 2000);
      } else {
        setError(
          result.error || t("views.shopifyImportModal.errors.importFailed"),
        );
      }
    } catch (err) {
      console.log(err);
      setError(t("views.shopifyImportModal.errors.unexpectedError"));
    } finally {
      setImporting(false);
    }
  };

  const resetModal = () => {
    setStep("select");
    setSelectedProduct(null);
    setConfig({
      commission: 15,
      margin: 30,
      category: "Espresso Blend",
      selectedVariants: [],
    });
    setError(null);
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(resetModal, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        testId={testId ?? setShopifyImportModalTestId("content")}
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Package className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>{t("views.shopifyImportModal.title")}</DialogTitle>
              <DialogDescription>
                {step === "select" &&
                  t("views.shopifyImportModal.stepDescriptions.select")}
                {step === "configure" &&
                  t("views.shopifyImportModal.stepDescriptions.configure")}
                {step === "review" &&
                  t("views.shopifyImportModal.stepDescriptions.review")}
              </DialogDescription>
            </div>
          </div>

          {/* Progress indicator */}
          <div className="flex items-center gap-2 pt-4">
            <div
              className={`flex items-center gap-2 ${step === "select" ? "text-primary" : "text-muted-foreground"}`}
            >
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${step === "select" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
              >
                1
              </div>
              <span className="text-sm font-medium">
                {t("views.shopifyImportModal.steps.select")}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div
              className={`flex items-center gap-2 ${step === "configure" ? "text-primary" : "text-muted-foreground"}`}
            >
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${step === "configure" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
              >
                2
              </div>
              <span className="text-sm font-medium">
                {t("views.shopifyImportModal.steps.configure")}
              </span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div
              className={`flex items-center gap-2 ${step === "review" ? "text-primary" : "text-muted-foreground"}`}
            >
              <div
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${step === "review" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
              >
                3
              </div>
              <span className="text-sm font-medium">
                {t("views.shopifyImportModal.steps.review")}
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="py-6">
          {/* Step 1: Select Products */}
          {step === "select" && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : error ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : (
                <div className="grid gap-4">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleSelectProduct(product)}
                      className="flex items-start gap-4 rounded-lg border p-4 text-left transition-colors hover:bg-muted/50"
                    >
                      <img
                        src={product.images[0] || "/placeholder.svg"}
                        alt={product.title}
                        style={{ width: "80px" }}
                        className="rounded-md object-cover"
                      />
                      <div className="flex-1 space-y-2">
                        <div>
                          <h3 className="font-semibold">{product.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {product.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold">
                            ${product.price.toFixed(2)}
                          </span>
                          {product.compareAtPrice && (
                            <span className="text-sm text-muted-foreground line-through">
                              ${product.compareAtPrice.toFixed(2)}
                            </span>
                          )}
                          <Badge variant="secondary">
                            {t("views.shopifyImportModal.variantCount", {
                              count: product.variants.length,
                            })}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {product.tags.slice(0, 3).map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className="text-xs"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Configure */}
          {step === "configure" && selectedProduct && (
            <div className="space-y-6">
              <div className="rounded-lg border p-4 bg-muted/30">
                <div className="flex items-start gap-4">
                  <img
                    src={selectedProduct.images[0] || "/placeholder.svg"}
                    alt={selectedProduct.title}
                    style={{ width: "80px" }}
                    className="rounded-md object-cover"
                  />
                  <div>
                    <h3 className="font-semibold">{selectedProduct.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedProduct.vendor}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">
                    {t("views.shopifyImportModal.labels.productTitle")}
                  </Label>
                  <Input
                    id="title"
                    value={config.title}
                    onChange={(e) =>
                      setConfig({ ...config, title: e.target.value })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    {t("views.shopifyImportModal.labels.description")}
                  </Label>
                  <Textarea
                    id="description"
                    value={config.description}
                    onChange={(e) =>
                      setConfig({ ...config, description: e.target.value })
                    }
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="price">
                      {t("views.shopifyImportModal.labels.price")}
                    </Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={config.price}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          price: Number.parseFloat(e.target.value),
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">
                      {t("views.shopifyImportModal.labels.category")}
                    </Label>
                    <Input
                      id="category"
                      value={config.category}
                      onChange={(e) =>
                        setConfig({ ...config, category: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="commission">
                      {t("views.shopifyImportModal.labels.commission")}
                    </Label>
                    <Input
                      id="commission"
                      type="number"
                      value={config.commission}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          commission: Number.parseInt(e.target.value),
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="margin">
                      {t("views.shopifyImportModal.labels.margin")}
                    </Label>
                    <Input
                      id="margin"
                      type="number"
                      value={config.margin}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          margin: Number.parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>
                    {t("views.shopifyImportModal.labels.variantsToImport")}
                  </Label>
                  <div className="space-y-2">
                    {selectedProduct.variants.map((variant) => (
                      <div
                        key={variant.id}
                        className="flex items-center space-x-2"
                      >
                        <Checkbox
                          id={variant.id}
                          checked={config.selectedVariants?.includes(
                            variant.id,
                          )}
                          onCheckedChange={(checked) => {
                            const variants = config.selectedVariants || [];
                            setConfig({
                              ...config,
                              selectedVariants: checked
                                ? [...variants, variant.id]
                                : variants.filter((id) => id !== variant.id),
                            });
                          }}
                        />
                        <label
                          htmlFor={variant.id}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {variant.title} - ${variant.price.toFixed(2)} (
                          {t("views.shopifyImportModal.inStock", {
                            count: variant.inventoryQuantity,
                          })}
                          )
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep("select")}>
                  <ChevronLeft className="mr-2 h-4 w-4" />
                  {t("buttons.back")}
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importing || !config.selectedVariants?.length}
                >
                  {importing && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {t("views.shopifyImportModal.importProduct")}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review/Success */}
          {step === "review" && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {t("views.shopifyImportModal.success.title")}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t("views.shopifyImportModal.success.description", {
                  title: config.title,
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("views.shopifyImportModal.success.redirecting")}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
